import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, X, FileText, Check, RefreshCw, Loader2, Clock } from 'lucide-react';

import { API_URL } from '../utils/api';
import { useLive } from '../context/LiveContext';


function _formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec} mp-e`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} perce`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} órája`;
    const days = Math.floor(h / 24);
    return `${days} napja`;
  } catch {
    return '—';
  }
}

export default function LocalPrintQueueButton() {
  const [stats, setStats] = useState({ pending_local: 0, last_imap_sync_at: null });
  const { live } = useLive();
  const [showModal, setShowModal] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/print/queue/stats`, {
        withCredentials: true,
        validateStatus: (s) => s < 500, // 401 nyitott oldalon ne dobjon error toast-ot
      });
      if (r.status === 200 && r.data) setStats(r.data);
    } catch {
      /* silent */
    }
  }, []);

  const fetchItems = useCallback(async (reset = true) => {
    setLoadingItems(true);
    try {
      const currentOffset = reset ? 0 : items.length;
      const r = await axios.get(
        `${API_URL}/api/print/queue/local-pending?limit=100&offset=${currentOffset}`,
        { withCredentials: true });
      const newItems = r.data || [];
      if (reset) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }
      // If we got fewer than limit, we're at the end
      setHasMore(newItems.length === 100);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'ismeretlen hiba';
      toast.error(`Nem sikerült betölteni a nyomtatási sort: ${detail}`);
    } finally {
      setLoadingItems(false);
    }
  }, [items.length]);

  const loadMore = useCallback(() => fetchItems(false), [fetchItems]);

  useEffect(() => {
    if (live?.print) setStats(live.print);
  }, [live]);

  useEffect(() => {
    if (showModal) fetchItems();
  }, [showModal, fetchItems]);

  const [syncingImap, setSyncingImap] = useState(false);
  const handleManualSync = async () => {
    setSyncingImap(true);
    try {
      const r = await axios.post(`${API_URL}/api/settings/imap/run-all`, {}, { withCredentials: true });
      const d = r.data;
      const errCount = (d.mailboxes || []).reduce((acc, m) => acc + (m.errors || []).length, 0);
      if (d.total_created > 0) {
        toast.success(`${d.total_created} új számla, ${d.total_queued_for_print || 0} nyomtatási sorba (${d.total_fetched} email átnézve)${errCount ? `, ${errCount} hiba` : ''}`, { duration: 8000 });
      } else if (d.total_fetched === 0) {
        toast.info('Egyetlen email sem volt a megadott időszakban');
      } else {
        toast.info(`${d.total_fetched} email átnézve, új számla nem volt (${d.total_skipped} már fel volt dolgozva)`);
      }
      // Stat azonnali frissítés a header badge-en
      fetchStats();
      // Modal lista is frissül ha nyitva van
      if (showModal) fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail?.includes('nincs beállítva')) {
        toast.error('Nincs beállított IMAP postafiók — Beállítások → Email intake');
      } else {
        toast.error(detail || 'IMAP frissítés hiba');
      }
    } finally {
      setSyncingImap(false);
    }
  };

  const [batchPrinting, setBatchPrinting] = useState(false);
  // iter 222 — Per-batch duplex override.
  // Empty string = 'auto' (use user's saved Print Settings).
  const [batchDuplex, setBatchDuplex] = useState('');
  const handleBatchPrint = async () => {
    if (count === 0) {
      toast.info('Nincs várakozó PDF');
      return;
    }
    setBatchPrinting(true);
    try {
      // GET → blob (axios kell hogy a session cookie megmenjen)
      const params = {};
      if (batchDuplex) params.duplex = batchDuplex;
      const r = await axios.get(`${API_URL}/api/print/queue/local/batch-pdf`, {
        withCredentials: true,
        responseType: 'blob',
        params,
      });
      const merged = parseInt(r.headers['x-acountix-merged-count'] || '0', 10);
      const skipped = parseInt(r.headers['x-acountix-skipped-count'] || '0', 10);
      const url = URL.createObjectURL(r.data);
      const win = window.open();
      if (!win) {
        toast.error('Pop-up blokkolva — engedélyezd ezt az oldalt a böngészőben');
        URL.revokeObjectURL(url);
        return;
      }
      win.document.write(`<!doctype html><html><head><title>Acountix Batch (${merged} db)</title></head>
        <body style="margin:0">
          <iframe src="${url}" style="width:100vw;height:100vh;border:0" onload="setTimeout(()=>{try{this.contentWindow.print()}catch(e){}}, 800)"></iframe>
        </body></html>`);
      win.document.close();
      toast.success(`${merged} PDF összefűzve egyetlen kötegbe${skipped > 0 ? ` (${skipped} kihagyva)` : ''}`, { duration: 6000 });
    } catch (err) {
      let detail = 'Batch nyomtatás hiba';
      if (err.response?.data instanceof Blob) {
        // The error response is JSON wrapped in a Blob (because responseType is 'blob')
        try {
          const txt = await err.response.data.text();
          const json = JSON.parse(txt);
          detail = json.detail || detail;
        } catch {
          detail = 'Nincs nyomtatható PDF a sorban';
        }
      } else {
        detail = err.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setBatchPrinting(false);
    }
  };

  const handlePrint = (item) => {
    // Új ablakban megnyitja a PDF-et (data: URL) és print dialógot indít
    if (!item.data) {
      toast.error('A PDF tartalom nem elérhető');
      return;
    }
    try {
      const win = window.open();
      if (!win) {
        toast.error('Pop-up blokkolva — engedélyezd ezt az oldalt a böngészőben');
        return;
      }
      win.document.write(`
        <!doctype html><html><head><title>${item.filename}</title></head>
        <body style="margin:0">
          <iframe src="${item.data}" style="width:100vw;height:100vh;border:0" onload="setTimeout(()=>{try{this.contentWindow.print()}catch(e){}}, 800)"></iframe>
        </body></html>
      `);
      win.document.close();
    } catch (e) {
      toast.error('Nyomtatás nem sikerült: ' + (e.message || 'ismeretlen hiba'));
    }
  };

  const handleMarkPrinted = async (id) => {
    setMarkingId(id);
    try {
      await axios.post(`${API_URL}/api/print/queue/${id}/mark-printed`, {}, { withCredentials: true });
      setItems(prev => prev.filter(x => x.id !== id));
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nem sikerült jelölni');
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAll = async () => {
    if (!window.confirm('Az összes várakozó nyomtatást "kinyomtatva"-ra jelölöd?')) return;
    setMarkingAll(true);
    try {
      const r = await axios.post(`${API_URL}/api/print/queue/local/mark-all-printed`, {}, { withCredentials: true });
      toast.success(`${r.data.marked || 0} jelölve kinyomtatottnak`);
      setItems([]);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nem sikerült');
    } finally {
      setMarkingAll(false);
    }
  };

  const count = stats.pending_local || 0;
  const lastSync = _formatRelative(stats.last_imap_sync_at);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        data-testid="local-print-queue-btn"
        className="relative p-2 rounded-lg transition-colors flex items-center gap-1.5 hidden md:flex"
        style={{ color: count > 0 ? 'var(--accent-copper)' : 'var(--text-secondary)' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--background-base)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={`Helyi nyomtatási sor — utolsó IMAP frissítés: ${lastSync}`}
        aria-label="Helyi nyomtatási sor"
      >
        <Printer size={18} strokeWidth={1.85} />
        {count > 0 && (
          <span
            data-testid="local-print-queue-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
            style={{ background: 'var(--accent-copper)', color: 'white' }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {showModal && createPortal(
        <div
          className="fixed inset-0 z-[1100] flex items-start md:items-center justify-center p-3 md:p-6"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowModal(false)}
          data-testid="local-print-queue-modal"
        >
          <div
            className="rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{ background: 'var(--background-surface)', border: '1px solid var(--border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: 'var(--accent-copper-soft)', color: 'var(--accent-copper)' }}>
                  <Printer size={18} />
                </div>                <div>
                  <h2 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Helyi nyomtatási sor</h2>
                  <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                    <Clock size={11} /> Utolsó IMAP frissítés: <strong>{lastSync}</strong>
                    {stats.last_imap_queued > 0 && <span> · {stats.last_imap_queued} új PDF</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleManualSync}
                  disabled={syncingImap}
                  data-testid="local-print-imap-sync"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: 'var(--accent-copper)', color: 'white' }}
                  title="IMAP postafiókok azonnali átvizsgálása (utolsó N email)"
                >
                  {syncingImap ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  {syncingImap ? 'Szinkron…' : 'IMAP sync most'}
                </button>
                <button
                  onClick={() => { fetchItems(); fetchStats(); }}
                  data-testid="local-print-refresh"
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  title="Csak a megjelenített lista újratöltése"
                >
                  {loadingItems ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  data-testid="local-print-close"
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="px-5 py-3 flex items-center justify-between text-sm border-b" style={{ borderColor: 'var(--border)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                {count === 0 ? 'Nincs várakozó nyomtatás' : `${count} PDF vár a nyomtatásra`}
              </span>
              {count > 0 && (
                <div className="flex items-center gap-2">
                  {/* iter 222 — per-batch duplex override (default = user's saved setting) */}
                  <select
                    value={batchDuplex}
                    onChange={(e) => setBatchDuplex(e.target.value)}
                    data-testid="local-print-duplex-select"
                    disabled={batchPrinting}
                    className="text-xs px-2 py-1.5 rounded-lg border bg-white disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }}
                    title="Nyomtatási mód a kötegre — felülírja a Beállítások alapértékét"
                  >
                    <option value="">Alap (Beállítások)</option>
                    <option value="simplex">Egyoldalas</option>
                    <option value="duplex_long">Kétoldalas (hosszú él)</option>
                    <option value="duplex_short">Kétoldalas (rövid él)</option>
                    <option value="off">Nincs jelölés</option>
                  </select>
                  <button
                    onClick={handleBatchPrint}
                    disabled={batchPrinting}
                    data-testid="local-print-batch"
                    className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: 'var(--accent-copper)', color: 'white' }}
                    title="Az összes PDF-et egyetlen kötegbe fűzi és egyszer nyomtatja"
                  >
                    {batchPrinting ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                    {batchPrinting ? 'Összefűzés…' : `Batch nyomtatás (${count})`}
                  </button>
                  <button
                    onClick={handleMarkAll}
                    disabled={markingAll}
                    data-testid="local-print-mark-all"
                    className="text-xs px-3 py-1.5 rounded-lg border disabled:opacity-50"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                  >
                    {markingAll ? 'Jelölés…' : 'Mindent jelöljek'}
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loadingItems && items.length === 0 && (
                <div className="flex items-center justify-center py-12" style={{ color: 'var(--text-secondary)' }}>
                  <Loader2 size={20} className="animate-spin mr-2" /> Betöltés…
                </div>
              )}
              {!loadingItems && items.length === 0 && (
                <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
                  <FileText size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Üres a nyomtatási sor.</p>
                  <p className="text-xs mt-1">Az IMAP-en keresztül érkező PDF számlák itt jelennek meg.</p>
                </div>
              )}
              {items.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 rounded-lg border"
                  style={{ borderColor: 'var(--border)', background: 'var(--background-base)' }}
                >
                  <FileText size={20} style={{ color: 'var(--accent-copper)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate" style={{ color: 'var(--text-primary)' }}>{item.filename}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {item.mailbox_label && <span>{item.mailbox_label} · </span>}
                      érkezett: {_formatRelative(item.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePrint(item)}
                    data-testid={`local-print-print-${item.id}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                    style={{ background: 'var(--accent-copper)', color: 'white' }}
                  >
                    <Printer size={13} /> Nyomtatás
                  </button>
                  <button
                    onClick={() => handleMarkPrinted(item.id)}
                    disabled={markingId === item.id}
                    data-testid={`local-print-done-${item.id}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 disabled:opacity-50"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                    title="Kinyomtattam (eltávolítás a sorból)"
                  >
                    {markingId === item.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Kész
                  </button>
                </div>
              ))}

              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={loadingItems}
                  data-testid="local-print-load-more"
                  className="w-full py-2 mt-2 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                >
                  {loadingItems ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  További 100 betöltése (jelenleg {items.length} elem)
                </button>
              )}
            </div>

            <div className="px-5 py-3 text-[11px] border-t" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
              Tipp: a &quot;Nyomtatás&quot; új ablakban megnyitja a PDF-et és elindítja a böngésző nyomtatás-párbeszédét. Engedélyezd a pop-up-okat ennek az oldalnak.
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
